import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError, retryWhen, concatMap } from 'rxjs/operators';
import { timer } from 'rxjs';
import { ApiConfigService } from './api-config.service';
import { UserProfile } from '../models/user.model';

/** Retry only on connection/timeout (status 0, 408, TimeoutError). Max 2 retries = 3 attempts total. */
function isConnectionOrTimeoutError(error: any): boolean {
  if (!error) return false;
  const status = error?.status ?? error?.originalError?.status;
  if (status === 0 || status === 408) return true;
  if (error?.name === 'TimeoutError') return true;
  const msg = (error?.message || error?.originalError?.message || '').toLowerCase();
  return msg.includes('timeout');
}

const AUTH_RETRY_DELAY_MS = 2500;
const AUTH_MAX_RETRIES = 2; // 2 retries = 3 total attempts

export interface LoginResponse {
  access_token: string;
  refresh_token?: string;
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    vendorId?: string;
    posLocationId?: string;
    serviceZoneId?: string;
  };
}

export interface VerifyOtpRequest {
  phone: string;
  code: string;
  name?: string;
  email?: string;
  role?: 'user' | 'vendor' | 'delivery';
}

@Injectable({ providedIn: 'root' })
export class ApiAuthService {
  private http = inject(HttpClient);
  private apiConfig = inject(ApiConfigService);
  private readonly TOKEN_KEY = 'cartco_jwt_token';
  private readonly REFRESH_TOKEN_KEY = 'cartco_refresh_token';
  private readonly USER_KEY = 'cartco_jwt_user';

  private get baseUrl(): string {
    return this.apiConfig.getApiUrl();
  }

  /**
   * Login with email and password (for admin users)
   */
  login(email: string, password: string): Observable<LoginResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http
      .post<LoginResponse>(`${this.baseUrl}/auth/login`, { email, password }, { headers })
      .pipe(
        retryWhen((errors) =>
          errors.pipe(
            concatMap((err, index) => {
              if (!isConnectionOrTimeoutError(err) || index >= AUTH_MAX_RETRIES) return throwError(() => err);
              return timer(AUTH_RETRY_DELAY_MS);
            }),
          ),
        ),
        tap((response) => {
          // Store tokens and user info (only if response is valid)
          if (response?.access_token && response?.user?.id) {
            this.setTokens(response.access_token, response.refresh_token);
            this.setUser(response.user);
          } else {
            console.warn('[ApiAuthService] Login response missing token/user; skipping token storage', response);
          }
        }),
        catchError((error) => {
          console.error('Login error:', error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Set or update password for an existing admin/ops user
   */
  setAdminPassword(email: string, password: string): Observable<{ message: string; user: any }> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http
      .post<{ message: string; user: any }>(`${this.baseUrl}/auth/set-admin-password`, { email, password }, { headers })
      .pipe(
        catchError((error) => {
          console.error('Set admin password error:', error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Request OTP for phone number (Login - checks user existence)
   */
  requestOtp(phone: string, role?: 'vendor' | 'delivery'): Observable<{ message: string; requiresRegistration?: boolean }> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const url = `${this.baseUrl}/auth/phone/request-otp`;
    const payload: { phone: string; role?: 'vendor' | 'delivery' } = { phone };
    if (role) {
      payload.role = role;
    }
    
    console.log('[ApiAuthService] Requesting OTP:', { url, phone });
    
    return this.http
      .post<{ message: string; requiresRegistration?: boolean }>(url, payload, { headers })
      .pipe(
        retryWhen((errors) =>
          errors.pipe(
            concatMap((err, index) => {
              if (!isConnectionOrTimeoutError(err) || index >= AUTH_MAX_RETRIES) return throwError(() => err);
              return timer(AUTH_RETRY_DELAY_MS);
            }),
          ),
        ),
        tap((response) => {
          console.log('[ApiAuthService] OTP request successful:', response);
        }),
        catchError((error) => {
          console.error('[ApiAuthService] Request OTP error:', error);
          console.error('[ApiAuthService] Error details:', {
            status: error?.status,
            statusText: error?.statusText,
            message: error?.message,
            error: error?.error,
          });
          return throwError(() => error);
        }),
      );
  }

  /**
   * Request OTP for phone number (Registration - no user check)
   */
  requestOtpForRegistration(phone: string): Observable<{ message: string }> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http
      .post<{ message: string }>(`${this.baseUrl}/auth/phone/register-request-otp`, { phone }, { headers })
      .pipe(
        retryWhen((errors) =>
          errors.pipe(
            concatMap((err, index) => {
              if (!isConnectionOrTimeoutError(err) || index >= AUTH_MAX_RETRIES) return throwError(() => err);
              return timer(AUTH_RETRY_DELAY_MS);
            }),
          ),
        ),
        catchError((error) => {
          console.error('Request OTP for registration error:', error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Verify OTP and login/register
   */
  verifyOtp(data: VerifyOtpRequest): Observable<LoginResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http
      .post<LoginResponse>(`${this.baseUrl}/auth/phone/verify-otp`, data, { headers })
      .pipe(
        retryWhen((errors) =>
          errors.pipe(
            concatMap((err, index) => {
              if (!isConnectionOrTimeoutError(err) || index >= AUTH_MAX_RETRIES) return throwError(() => err);
              return timer(AUTH_RETRY_DELAY_MS);
            }),
          ),
        ),
        tap((response) => {
          // Store tokens and user info (only if response is valid)
          // NOTE: In offline/network-failure scenarios, some interceptors may return a non-LoginResponse body.
          if (response?.access_token && response?.user?.id) {
            this.setTokens(response.access_token, response.refresh_token);
            this.setUser(response.user);
          } else {
            console.warn('[ApiAuthService] Verify OTP response missing token/user; skipping token storage', response);
          }
        }),
        catchError((error) => {
          console.error('Verify OTP error:', error);
          return throwError(() => error);
        }),
      );
  }

  getToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(this.TOKEN_KEY);
  }

  setToken(token: string): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  setTokens(accessToken: string, refreshToken?: string): void {
    this.setToken(accessToken);
    if (refreshToken && typeof window !== 'undefined') {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  getRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  clearToken(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }

  clearTokens(): void {
    this.clearToken();
  }

  getUser(): any | null {
    if (typeof window === 'undefined') return null;
    const userStr = localStorage.getItem(this.USER_KEY);
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  setUser(user: any): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    
    // Validate token is not expired
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      // Check if token expires in more than 30 seconds (reduced buffer for better user experience)
      return Date.now() < exp - 30000;
    } catch {
      return false;
    }
  }

  /**
   * Convert JWT user to UserProfile format
   */
  convertToUserProfile(jwtUser: any): UserProfile {
    return {
      id: jwtUser.id,
      email: jwtUser.email,
      name: jwtUser.name,
      role: jwtUser.role as any,
      phone: jwtUser.phone || '',
      photoUrl: jwtUser.photoUrl || '',
      vendorId: jwtUser.vendorId,
      posLocationId: jwtUser.posLocationId,
      serviceZoneId: jwtUser.serviceZoneId,
      accountStatus: 'active',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
  }

  /**
   * Refresh access token using refresh token
   */
  refreshToken(): Observable<LoginResponse> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http.post<LoginResponse>(`${this.baseUrl}/auth/refresh`, {
      refresh_token: refreshToken,
    }).pipe(
      tap((response) => {
        this.setTokens(response.access_token, response.refresh_token);
        if (response.user) {
          this.setUser(response.user);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        const status = error?.status ?? 0;

        // If refresh token is invalid/expired/revoked, clear tokens (forces re-login).
        if (status === 401) {
          this.clearTokens();
          return throwError(() => error);
        }

        // IMPORTANT: For transient errors (network/timeout/server), keep tokens.
        // This prevents "login again and again" loops when connectivity is flaky.
        if (status === 0 || status === 408 || status >= 500) {
          console.warn('[ApiAuthService] Token refresh failed (transient), keeping existing tokens', {
            status,
            message: error?.message,
          });
          return throwError(() => error);
        }

        console.warn('[ApiAuthService] Token refresh failed (non-401), keeping existing tokens', {
          status,
          message: error?.message,
        });
        return throwError(() => error);
      })
    );
  }

  /**
   * Logout - revoke refresh token
   */
  logout(): Observable<void> {
    const refreshToken = this.getRefreshToken();
    if (refreshToken) {
      return this.http.post<void>(`${this.baseUrl}/auth/logout`, {
        refresh_token: refreshToken,
      }).pipe(
        tap(() => {
          this.clearTokens();
        }),
        catchError((error) => {
          // Clear tokens even if logout request fails
          this.clearTokens();
          return throwError(() => error);
        })
      );
    }
    // If no refresh token, just clear local storage
    this.clearTokens();
    return new Observable((observer) => {
      observer.next();
      observer.complete();
    });
  }

}

